import {
  Container,
  Card,
  CardHeader,
  CardContent,
  Box,
  Button,
  useMediaQuery,
  LinearProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { ArrowForward } from "@material-ui/icons";
import { FormBuilder } from "@zennya/web-component-library/src/components/FormBuilder";
import { ConcentricBadge } from "components/ConcentricBadge";
import { motion } from "framer-motion";
import { debounce, isEmpty } from "lodash";
import moment from "moment-timezone";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { JsonParam, StringParam } from "serialize-query-params";
import corporateHealthService from "services/corporate-health.service";
import { useQueryParam } from "use-query-params";
import { processHealthPass } from "util/ProcessHealthPass";
import { slideRight, slideUp } from "util/transitions";

import * as Yup from "yup";
import { useStyles as baseCardStyles } from "../MedicalId/index";

const useStyles = makeStyles((theme) => ({}));

function AdditionalMedicalInfo() {
  const classes = { ...useStyles(), ...baseCardStyles() };
  const history = useHistory();
  const location = useLocation();

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("xs"), {
    noSsr: true,
  });

  const [urlFormValues, setUrlFormValues] = useQueryParam(
    "additionalInfo",
    JsonParam
  );

  console.log("📢[index.js:40]:", urlFormValues);
  const [formValues, setFormValues] = useState(urlFormValues);

  const [allergies, setAllergies] = useState([]);
  const [comorbidities, setComorbidities] = useState([]);
  const [vaccines, setVaccines] = useState([]);

  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const backButtonProps = {
    variant: isSmallScreen ? "text" : "contained",
  };

  const debounceParams = useCallback(
    debounce(
      (values) => {
        setUrlFormValues(values, "replaceIn");
      },
      250,
      { maxWait: 500 }
    ),
    []
  );

  useEffect(() => {
    debounceParams(formValues);
  }, [debounceParams, formValues, setUrlFormValues]);

  useEffect(() => {
    async function fetchMedicalStatuses() {
      setAllergies((await corporateHealthService.getAllergies())?.data?.list);
      setComorbidities(
        (await corporateHealthService.getComorbidities())?.data?.list
      );
      setVaccines((await corporateHealthService.getVaccines())?.data?.list);
    }
    fetchMedicalStatuses();
  }, []);

  const formLayout = useMemo(() => {
    return [
      "comorbidities",
      "allergies",
      "uploadCertificate",
      "hasFirstDose",
      "firstDoseDate",
      "vaccine",
      "hasSecondDose",
      "secondDoseDate",
    ];
  }, []);

  const form = useMemo(() => {
    return {
      allergies: {
        type: "autocomplete",
        label: "Allergy",
        initialValues: formValues?.allergies ?? [],
        options: allergies,
        settings: {
          default: "",
          multiple: true,
          labelField: "label",
          valueField: "id",
        },
        validator: () => Yup.array().label(" "),
        // readOnly: formValues?.allergies,
        placeholder: 'Type to search or leave blank if not applicable',
        fieldProps: {
          className: classes.default,
        },
      },
      comorbidities: {
        type: "autocomplete",
        label: "Comorbidity",
        initialValues: formValues?.comorbidities ?? [],
        options: comorbidities,
        settings: {
          default: "",
          multiple: true,
          labelField: "label",
          valueField: "id",
        },
        validator: () => Yup.array().label(" "),
        // readOnly: formValues?.comorbidities,
        placeholder: 'Type to search or leave blank if not applicable',
        fieldProps: {
          className: classes.default,
        },
      },
      hasFirstDose: {
        type: "radio",
        label:
          "Has the vaccinee previously received a COVID-19 Vaccine? If yes, specify",
        initialValues: formValues?.hasFirstDose ?? "false",
        options: [
          { value: "true", label: "Yes" },
          { value: "false", label: "No" },
        ],
        settings: {
          labelField: "label",
          valueField: "value",
          inline: true,
        },
        validator: () => Yup.bool().required(),
        // onChange: (field, value) => {
        //   setFormValues({ ...formValues, [field]: value });
        // },
      },
      hasSecondDose: {
        type: "radio",
        label: "Have you received your second dose?",
        initialValues: formValues?.hasSecondDose ?? "false",
        options: [
          { value: "true", label: "Yes" },
          { value: "false", label: "No" },
        ],
        settings: {
          labelField: "label",
          valueField: "value",
          inline: true,
        },
        validator: () => Yup.bool().required(),
        // onChange: (field, value) => {
        //   setFormValues({ ...formValues, [field]: value });
        // },
        hidden: formValues?.hasFirstDose === "false",
      },
      firstDoseDate: {
        type: "date",
        label: "Date of first dose",
        disableFuture: true,
        disablePast: false,
        initialValues: formValues?.firstDoseDate ?? new Date(),
        useLocalTime: true,
        validator: () => Yup.date().max(new Date()),
        hidden: formValues?.hasFirstDose === "false",
      },
      secondDoseDate: {
        type: "date",
        label: "Date of second dose",
        disableFuture: true,
        disablePast: false,
        initialValues: formValues?.secondDoseDate ?? new Date(),
        useLocalTime: true,
        validator: () => Yup.date().max(new Date()),
        hidden:
          formValues?.hasFirstDose === "false"
            ? true
            : formValues?.hasSecondDose === "false",
      },
      vaccine: {
        type: "autocomplete",
        label: "Vaccine",
        initialValues: formValues?.vaccine ?? "",
        options: vaccines,
        settings: {
          default: "",
          multiple: false,
          labelField: "label",
          valueField: "name",
        },
        hidden: formValues?.hasFirstDose === "false",
        validator: () => {
          return formValues?.hasFirstDose === "true"
            ? Yup.object().nullable().label(" ").required()
            : Yup.object();
        },
        fieldProps: {
          className: classes.default,
        },
      },
    };
  }, [allergies, comorbidities, formValues, vaccines]);

  const setPersistedValues = useCallback((values) => {
    console.log("📢[index.js:139persistedvalues]:", values);
    setFormValues(values);
  }, []);

  async function handleSubmit(values) {
    const mapped = { ...values };
    mapped["secondDoseDate"] = moment(mapped.secondDoseDate).tz("Asia/Manila").toDate();
    mapped["firstDoseDate"] = moment(mapped.firstDoseDate).tz("Asia/Manila").toDate();
    if (values.hasFirstDose === "false") {
      delete mapped["secondDoseDate"];
      delete mapped["vaccine"];
      delete mapped["firstDoseDate"];
    }
    if (values?.hasSecondDose === "false") {
      delete mapped["secondDoseDate"];
    }
    delete mapped["hasSecondDose"];
    delete mapped["hasFirstDose"];
    console.log("📢[index.js:179 postprocessform]:", mapped);
    history.push({
      pathname: "/onboarding/finalize",
      search: location.search,
    });
  }
  return (
    <motion.div {...(isMobile ? slideRight : slideUp)}>
      <Container fixed maxWidth="md" className={classes.cardInset}>
        <Box className={classes.headerContainer}>
          <ConcentricBadge number="4" />
          <span className={classes.header}>Additional Medical Information</span>
        </Box>
        <Card className={classes.card}>
          <CardContent>
            <FormBuilder
              formId="additionalMedicalInfo"
              // usePersist
              onChange={(values) => {
                setFormValues(values);
              }}
              variant="outlined"
              formLabel=" "
              submitLabel={
                <>
                  Next <ArrowForward fontSize="small" />
                </>
              }
              formFactor="default"
              hasReset={false}
              formLayout={formLayout}
              form={form}
              onSubmit={(values) => {
                handleSubmit(values);
              }}
              additionalActions={() => (
                <Button {...backButtonProps} onClick={() => history.goBack()}>
                  Back
                </Button>
              )}
              // onPersistLoad={(values) => setPersistedValues(values)}
              // onReset={(values) => setPersistedValues(values)}
            />
          </CardContent>
        </Card>
      </Container>
    </motion.div>
  );
}

export default function AdditionalMedicalInfoLoader() {
  const [loading, setLoading] = useState(true);
  const [hashId, setHashId] = useQueryParam("code", StringParam);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("xs"), {
    noSsr: true,
  });
  const [urlFormValues, setUrlFormValues] = useQueryParam(
    "additionalInfo",
    JsonParam
  );

  useEffect(() => {
    async function fetchHealthPass() {
      if (isEmpty(urlFormValues)) {
        const healthPass = await processHealthPass(hashId);
        setUrlFormValues(healthPass?.additionalInfo, "replaceIn");
      }
      setLoading(false);
    }
    fetchHealthPass();
  }, [hashId, setUrlFormValues, urlFormValues]);

  if (loading) {
    return <LinearProgress />;
  }
  return (
    <motion.div {...(isMobile ? slideRight : slideUp)}>
      <AdditionalMedicalInfo />
    </motion.div>
  );
}
